import React, { useEffect, useRef, useState } from 'react';
import TopBar from 'components/TopBar/TopBar';
import Copyright from 'components/Copyright/Copyright';
import routes from 'routes/routes';
import {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	IntroMeta,
	Layout,
	TocNav,
	TocLabel,
	TocList,
	TocItem,
	TocLink,
	TocNum,
	Main,
	Article,
	ArticleHeading,
	ArticleNum,
	P,
	List,
	Address,
	BackToTop,
} from 'components/LegalDocument/styled';

const SECTIONS = [
	{ id: 'acceptance', num: '1', title: 'Acceptance of These Terms' },
	{ id: 'the-service', num: '2', title: 'The Service' },
	{ id: 'eligibility', num: '3', title: 'Eligibility and Institutional Access' },
	{ id: 'your-account', num: '4', title: 'Your Account' },
	{ id: 'acceptable-use', num: '5', title: 'Acceptable Use' },
	{ id: 'user-content', num: '6', title: 'User Content' },
	{ id: 'marketplace-jobs', num: '7', title: 'Marketplace, Jobs, and Third-Party Interactions' },
	{ id: 'office-hours', num: '8', title: 'Office Hours and Academic Content' },
	{ id: 'intellectual-property', num: '9', title: 'Intellectual Property' },
	{ id: 'third-party-services', num: '10', title: 'Third-Party Services' },
	{ id: 'termination', num: '11', title: 'Termination' },
	{ id: 'disclaimers', num: '12', title: 'Disclaimers and Limitation of Liability' },
	{ id: 'apple-terms', num: '13', title: 'Additional Terms for the Apple App Store' },
	{ id: 'governing-law', num: '14', title: 'Governing Law and Disputes' },
	{ id: 'changes', num: '15', title: 'Changes to These Terms' },
	{ id: 'contact', num: '16', title: 'Contact Us' },
];

const Terms = () => {
	const [activeId, setActiveId] = useState(SECTIONS[0].id);
	const sectionRefs = useRef({});

	useEffect(() => {
		document.title = 'Terms of Service | Flass';
	}, []);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) setActiveId(entry.target.id);
				});
			},
			{ rootMargin: '-15% 0px -70% 0px', threshold: 0 },
		);
		SECTIONS.forEach(({ id }) => {
			const el = sectionRefs.current[id];
			if (el) observer.observe(el);
		});
		return () => observer.disconnect();
	}, []);

	return (
		<Wrapper id="top">
			<TopBar />

			<Intro>
				<IntroEyebrow>Legal</IntroEyebrow>
				<IntroTitle>Terms of Service</IntroTitle>
				<IntroLead>
					The terms that govern your use of Flass — read alongside
					our{' '}
					<a
						href={routes.PRIVACY}
						style={{ color: 'inherit', textDecoration: 'underline' }}
					>
						Privacy Policy
					</a>
					, which explains what we collect and why.
				</IntroLead>
				<IntroMeta>
					<span>Effective August 8, 2026</span>
					<span>Updated August 8, 2026</span>
				</IntroMeta>
			</Intro>

			<Layout>
				<TocNav aria-label="Terms of service sections">
					<TocLabel>Contents</TocLabel>
					<TocList>
						{SECTIONS.map((section) => (
							<TocItem key={section.id}>
								<TocLink
									href={`#${section.id}`}
									$active={activeId === section.id}
								>
									<TocNum>{section.num}</TocNum>
									{section.title}
								</TocLink>
							</TocItem>
						))}
					</TocList>
				</TocNav>

				<Main>
					<P>
						These Terms of Service (&ldquo;
						<strong>Terms</strong>&rdquo;) are a legal agreement
						between you and Ergi Laze, the individual developer of
						Flass (&ldquo;<strong>we</strong>,&rdquo; &ldquo;
						<strong>us</strong>,&rdquo; or &ldquo;
						<strong>our</strong>&rdquo;), governing your use of
						the Flass mobile application, website, and related
						services (collectively, the &ldquo;
						<strong>Service</strong>&rdquo;).
					</P>
					<P>
						By creating an account, logging in through your
						Institution, or otherwise using the Service, you
						agree to be bound by these Terms. If you do not
						agree, do not use the Service.
					</P>

					<Article
						id="acceptance"
						ref={(el) => (sectionRefs.current['acceptance'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>1</ArticleNum>Acceptance of These Terms
						</ArticleHeading>
						<P>
							These Terms apply to all users of the Service,
							including students, lecturers, staff, and
							administrators accessing Flass through a
							participating educational institution (&ldquo;
							<strong>Institution</strong>&rdquo;) or by
							creating an account directly. If you are using
							the Service on behalf of an Institution, you
							represent that you have the authority to bind
							that Institution to any additional terms agreed
							separately between us and the Institution, and
							that those additional terms take precedence over
							these Terms where they conflict.
						</P>
					</Article>

					<Article
						id="the-service"
						ref={(el) => (sectionRefs.current['the-service'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>2</ArticleNum>The Service
						</ArticleHeading>
						<P>
							Flass is a campus communication platform that
							provides course and community rooms, direct
							messaging, polls and surveys, events, a
							marketplace, job and internship postings, and an
							office-hours queue system for students and staff
							at participating Institutions. We may add,
							change, or remove features at any time, and we do
							not guarantee that any specific feature will
							continue to be available.
						</P>
					</Article>

					<Article
						id="eligibility"
						ref={(el) => (sectionRefs.current['eligibility'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>3</ArticleNum>Eligibility and Institutional Access
						</ArticleHeading>
						<P>
							The Service is intended for individuals affiliated
							with a post-secondary educational Institution —
							students, lecturers, staff, and administrators —
							and is not directed to children under the age of
							13 (or the applicable minimum age in your
							jurisdiction). By using the Service, you represent
							that you meet this description and that all
							registration information you provide is accurate.
						</P>
						<P>
							Access through an Institution&rsquo;s Moodle or
							single sign-on integration is provisioned and
							revoked according to your standing with that
							Institution. We are not responsible for
							enrollment, role, or roster decisions made by your
							Institution, and your access to Institution-linked
							rooms may end automatically if your Institution
							reports that your affiliation has ended.
						</P>
					</Article>

					<Article
						id="your-account"
						ref={(el) => (sectionRefs.current['your-account'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>4</ArticleNum>Your Account
						</ArticleHeading>
						<List>
							<li>
								You are responsible for maintaining the
								confidentiality of your login credentials and
								for all activity that occurs under your
								account.
							</li>
							<li>
								Notify us promptly at ergilaze@gmail.com if you
								suspect unauthorized use of your account.
							</li>
							<li>
								You may not share your account, impersonate
								another person, or create an account for
								anyone other than yourself.
							</li>
							<li>
								Information you provide during registration or
								in your profile must be accurate and kept up
								to date.
							</li>
						</List>
					</Article>

					<Article
						id="acceptable-use"
						ref={(el) => (sectionRefs.current['acceptable-use'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>5</ArticleNum>Acceptable Use
						</ArticleHeading>
						<P>You agree not to use the Service to:</P>
						<List>
							<li>
								Harass, threaten, bully, or discriminate
								against another user, in a group room, a
								server room, a private conversation, or
								anywhere else in the Service;
							</li>
							<li>
								Post content that is unlawful, defamatory,
								obscene, or that infringes another
								person&rsquo;s intellectual property or
								privacy rights;
							</li>
							<li>
								Impersonate a lecturer, staff member,
								administrator, or another student, or
								otherwise misrepresent your affiliation with
								an Institution;
							</li>
							<li>
								Post fraudulent, misleading, or prohibited
								listings in the marketplace or job board,
								including listings for items or services you
								are not authorized to offer;
							</li>
							<li>
								Attempt to gain unauthorized access to another
								user&rsquo;s account, another Institution&rsquo;s
								data, or the Service&rsquo;s underlying
								systems;
							</li>
							<li>
								Upload malicious code, scrape or bulk-collect
								data beyond your own account&rsquo;s
								information, or interfere with the Service&rsquo;s
								normal operation;
							</li>
							<li>
								Use the office-hours queue, event RSVPs, or
								polls/surveys to disrupt or misrepresent an
								academic process.
							</li>
						</List>
						<P>
							We may remove content or suspend or terminate an
							account that violates this section, as described
							in Section 11.
						</P>
					</Article>

					<Article
						id="user-content"
						ref={(el) => (sectionRefs.current['user-content'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>6</ArticleNum>User Content
						</ArticleHeading>
						<P>
							&ldquo;<strong>User Content</strong>&rdquo; means
							messages, files, photos, polls, surveys, events,
							marketplace listings, job postings, and any other
							content you submit to the Service. You retain
							ownership of your User Content.
						</P>
						<P>
							By submitting User Content, you grant us a
							worldwide, non-exclusive, royalty-free license to
							host, store, transmit, reproduce, and display
							that content solely as necessary to operate,
							maintain, and provide the Service to you and the
							other members of the rooms you share it in — for
							example, delivering a message to the other
							members of a group, or displaying a marketplace
							listing to other users at your Institution. This
							license ends when you delete the content or your
							account, except where a copy reasonably persists
							in another user&rsquo;s message history (for
							example, a message you sent that another group
							member has not deleted) or where retention is
							required under Section 6 of our Privacy Policy.
						</P>
						<P>
							You are solely responsible for your User Content
							and for having the rights necessary to post it.
							We do not pre-screen User Content, but we may
							remove it, or restrict your access to the
							Service, if we believe in good faith that it
							violates these Terms, the law, or a right of a
							third party.
						</P>
					</Article>

					<Article
						id="marketplace-jobs"
						ref={(el) => (sectionRefs.current['marketplace-jobs'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>7</ArticleNum>Marketplace, Jobs, and Third-Party Interactions
						</ArticleHeading>
						<P>
							The marketplace and job/internship board let users
							post listings and contact each other directly.
							Flass is a venue for these listings, not a party
							to any transaction, employment relationship, or
							agreement that results from them.
						</P>
						<List>
							<li>
								We do not verify the accuracy of listings,
								the identity or credentials of a poster, or
								the legitimacy of an offered item, service, or
								job;
							</li>
							<li>
								We are not responsible for the quality,
								safety, legality, or delivery of anything
								bought, sold, traded, or offered through the
								Service, or for any dispute between users
								arising from a listing;
							</li>
							<li>
								Any transaction or agreement you enter into
								with another user is solely between you and
								that user, at your own risk.
							</li>
						</List>
						<P>
							Report a listing you believe is fraudulent or
							violates these Terms so we can review it.
						</P>
					</Article>

					<Article
						id="office-hours"
						ref={(el) => (sectionRefs.current['office-hours'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>8</ArticleNum>Office Hours and Academic Content
						</ArticleHeading>
						<P>
							The office-hours queue, polls, surveys, and event
							tools are communication aids provided to make
							coordination easier — they are not your
							Institution&rsquo;s official record-keeping,
							attendance, or assessment system, and are not a
							substitute for it. Where anything in the Service
							conflicts with an Institution&rsquo;s own academic
							policies, the Institution&rsquo;s policies govern.
							We are not responsible for academic decisions made
							using information from the Service.
						</P>
					</Article>

					<Article
						id="intellectual-property"
						ref={(el) =>
							(sectionRefs.current['intellectual-property'] = el)
						}
					>
						<ArticleHeading>
							<ArticleNum>9</ArticleNum>Intellectual Property
						</ArticleHeading>
						<P>
							Except for User Content, the Service — including
							its software, design, text, graphics, and the
							Flass name and logo — is owned by Ergi Laze or
							licensed to us, and is protected by intellectual
							property law. We grant you a limited,
							non-exclusive, non-transferable, revocable license
							to use the Service for its intended purpose,
							subject to these Terms. You may not copy, modify,
							distribute, sell, or reverse-engineer any part of
							the Service except to the extent applicable law
							expressly permits it.
						</P>
					</Article>

					<Article
						id="third-party-services"
						ref={(el) =>
							(sectionRefs.current['third-party-services'] = el)
						}
					>
						<ArticleHeading>
							<ArticleNum>10</ArticleNum>Third-Party Services
						</ArticleHeading>
						<P>
							The Service relies on third-party infrastructure
							providers described in Section 5.3 of our{' '}
							<a
								href={routes.PRIVACY}
								style={{ color: 'inherit', textDecoration: 'underline' }}
							>
								Privacy Policy
							</a>{' '}
							to operate (database hosting, file storage,
							push notifications, email, and related services),
							and may link to or integrate with third-party
							websites or your Institution&rsquo;s own systems.
							We are not responsible for the content,
							availability, or practices of third-party
							services, and your use of them may be subject to
							their own terms.
						</P>
					</Article>

					<Article
						id="termination"
						ref={(el) => (sectionRefs.current['termination'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>11</ArticleNum>Termination
						</ArticleHeading>
						<P>
							You may stop using the Service and request
							deletion of your account at any time by
							contacting ergilaze@gmail.com. We may suspend or
							terminate your access to the Service, with or
							without notice, if we believe in good faith that
							you have violated these Terms, created risk or
							legal exposure for us or another user, or if your
							Institution reports that your affiliation with it
							has ended. Sections of these Terms that by their
							nature should survive termination — including
							Sections 6, 9, 12, and 14 — will continue to
							apply after your account is closed.
						</P>
					</Article>

					<Article
						id="disclaimers"
						ref={(el) => (sectionRefs.current['disclaimers'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>12</ArticleNum>Disclaimers and Limitation of Liability
						</ArticleHeading>
						<P>
							THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND
							&ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTIES OF
							ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
							INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS
							FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR
							THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR
							ERROR-FREE.
						</P>
						<P>
							TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
							ERGI LAZE WILL NOT BE LIABLE FOR ANY INDIRECT,
							INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
							DAMAGES, OR ANY LOSS OF DATA, USE, GOODWILL, OR
							PROFITS, ARISING FROM YOUR USE OF THE SERVICE OR
							ANY CONTENT, TRANSACTION, OR INTERACTION WITH
							ANOTHER USER, EVEN IF ADVISED OF THE POSSIBILITY
							OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM
							ARISING FROM THESE TERMS OR THE SERVICE WILL NOT
							EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US,
							IF ANY, IN THE 12 MONTHS BEFORE THE CLAIM AROSE,
							OR (B) 50 EUROS. SOME JURISDICTIONS DO NOT ALLOW
							THESE LIMITATIONS, SO THEY MAY NOT APPLY TO YOU IN
							FULL.
						</P>
						<P>
							You agree to indemnify and hold Ergi Laze harmless
							from any claim, loss, or expense (including
							reasonable legal fees) arising from your User
							Content, your violation of these Terms, or your
							violation of another person&rsquo;s rights.
						</P>
					</Article>

					<Article
						id="apple-terms"
						ref={(el) => (sectionRefs.current['apple-terms'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>13</ArticleNum>Additional Terms for the Apple App Store
						</ArticleHeading>
						<P>
							If you downloaded the Flass app from Apple&rsquo;s
							App Store, the following additional terms apply
							and take precedence over any conflicting term
							above:
						</P>
						<List>
							<li>
								This agreement is between you and Ergi Laze
								only, not Apple, and Ergi Laze — not Apple —
								is solely responsible for the app and its
								content.
							</li>
							<li>
								The app may only be used on Apple-branded
								products that you own or control, as permitted
								by the App Store&rsquo;s Usage Rules.
							</li>
							<li>
								Apple has no obligation to provide maintenance
								or support for the app.
							</li>
							<li>
								In the event of any failure of the app to
								conform to an applicable warranty, you may
								notify Apple, and Apple will refund the
								purchase price for the app, if any, to you;
								to the maximum extent permitted by law, Apple
								has no other warranty obligation with respect
								to the app, and any other claims, losses,
								liabilities, damages, costs, or expenses
								attributable to a failure to conform to a
								warranty are Ergi Laze&rsquo;s responsibility
								alone.
							</li>
							<li>
								Ergi Laze, not Apple, is responsible for
								addressing any claims relating to the app or
								your possession or use of it, including
								product liability claims, legal or regulatory
								compliance claims, and consumer protection
								claims.
							</li>
							<li>
								If a third party claims the app infringes its
								intellectual property rights, Ergi Laze, not
								Apple, is solely responsible for the
								investigation, defense, and resolution of
								that claim.
							</li>
							<li>
								You must comply with any applicable third-party
								agreement (for example, your wireless data
								service agreement) when using the app.
							</li>
							<li>
								You represent that you are not located in a
								country subject to a U.S. government embargo
								and are not on any U.S. government list of
								prohibited or restricted parties.
							</li>
							<li>
								Apple and Apple&rsquo;s subsidiaries are
								third-party beneficiaries of these Terms, and
								upon your acceptance, Apple has the right to
								enforce these Terms against you as a
								third-party beneficiary.
							</li>
						</List>
					</Article>

					<Article
						id="governing-law"
						ref={(el) => (sectionRefs.current['governing-law'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>14</ArticleNum>Governing Law and Disputes
						</ArticleHeading>
						<P>
							These Terms are governed by the laws of Albania,
							without regard to its conflict-of-law principles,
							except where applicable consumer-protection law in
							your own country of residence gives you the
							benefit of mandatory local protections that
							cannot be waived by contract. Any dispute arising
							from these Terms or the Service that cannot be
							resolved informally will be subject to the
							exclusive jurisdiction of the courts of Albania,
							unless applicable law requires otherwise.
						</P>
						<P>
							Before filing a claim, please contact us at
							ergilaze@gmail.com — most concerns can be resolved
							directly.
						</P>
					</Article>

					<Article
						id="changes"
						ref={(el) => (sectionRefs.current['changes'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>15</ArticleNum>Changes to These Terms
						</ArticleHeading>
						<P>
							We may update these Terms from time to time to
							reflect changes to the Service or for legal,
							operational, or regulatory reasons. We will post
							the updated Terms with a new &ldquo;Last
							updated&rdquo; date, and where changes are
							material, we will provide additional notice (such
							as an in-app notification or email) before they
							take effect. Continued use of the Service after
							changes take effect constitutes acceptance of the
							updated Terms.
						</P>
					</Article>

					<Article
						id="contact"
						ref={(el) => (sectionRefs.current['contact'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>16</ArticleNum>Contact Us
						</ArticleHeading>
						<P>
							If you have questions about these Terms, please
							contact us at:
						</P>
						<Address>
							Ergi Laze
							<br />
							Flass
							<br />
							ergilaze@gmail.com
						</Address>
					</Article>

					<BackToTop href="#top">↑ Back to top</BackToTop>
				</Main>
			</Layout>

			<Copyright />
		</Wrapper>
	);
};

export default Terms;
